import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { legacyApi, CreditNote } from '../../../services/legacyApi';
import { Button } from '../../../components/ui/button';
import { CompactTable } from '../../../components/ui/CompactTable';
import { ArrowLeft, Download, Link2, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { SendPdfModal } from '../../../components/SendPdfModal';
import { openWhatsAppMessage } from '../utils/whatsapp';

export const LegacyCreditNoteDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [creditNote, setCreditNote] = useState<CreditNote | null>(null);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);
    const { hasPermission } = useAuth();

    const refresh = async () => {
        if (!id) return;
        const data = await legacyApi.sales.getCreditNote(Number(id));
        setCreditNote(data);
    };

    useEffect(() => {
        refresh()
            .catch((err) => toast.error(err?.message || 'Failed to load credit note'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleTransition = async (action: 'post' | 'cancel') => {
        if (!creditNote) return;
        setBusy(action);
        try {
            await legacyApi.sales.transitionCreditNote(creditNote.cn_id, action);
            await refresh();
            toast.success(`Credit note ${action}ed successfully`);
        } catch (err: any) {
            toast.error(err?.message || `Failed to ${action} credit note`);
        } finally {
            setBusy(null);
        }
    };

    const resolveCreditNoteRecipientEmail = async () => {
        if (!creditNote?.customer_id) return '';
        const customers = await legacyApi.customers.list();
        return customers.find((customer) => Number(customer.customer_id) === Number(creditNote.customer_id))?.email_address || '';
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!creditNote) return;
        const result = await legacyApi.sales.emailCreditNotePdf(creditNote.cn_id, payload);
        toast.success(result.message || 'Credit note PDF emailed successfully');
    };

    const lineColumns = useMemo(() => ([
        { header: 'Item', accessor: (row: any) => row.part_name, className: 'min-w-[240px]' },
        { header: 'Qty', accessor: (row: any) => Number(row.quantity || 0).toFixed(3), className: 'text-right font-mono' },
        { header: 'Unit Price', accessor: (row: any) => Number(row.unit_price || 0).toFixed(2), className: 'text-right font-mono' },
        { header: 'Line Total', accessor: (row: any) => Number(row.line_total || 0).toFixed(2), className: 'text-right font-mono font-medium' },
    ]), []);

    const historyColumns = useMemo(() => ([
        { header: 'Date', accessor: (row: any) => row.created_at ? new Date(row.created_at).toLocaleString() : '-', className: 'whitespace-nowrap' },
        { header: 'From', accessor: (row: any) => row.old_status_name || '-', className: 'whitespace-nowrap' },
        { header: 'To', accessor: (row: any) => row.new_status_name || '-', className: 'whitespace-nowrap' },
        { header: 'Reason', accessor: (row: any) => row.reason || '-', className: 'min-w-[220px]' },
    ]), []);

    if (loading) return <div className="p-4">Loading credit note...</div>;
    if (!creditNote) return <div className="p-4">Credit note not found.</div>;

    const isDraft = Number(creditNote.status_id || 1) === 1;
    const creditNoteNumber = creditNote.cn_number || `HC/CN/${String(creditNote.cn_id).padStart(4, '0')}`;

    const handleShareLink = async () => {
        if (!creditNote) return;
        try {
            const result = await legacyApi.sales.createCreditNoteShareLink(creditNote.cn_id);
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Credit note share link created and copied' : 'Credit note share link copied');
            } catch {
                window.prompt('Copy credit note share link', result.share_url);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create credit note share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!creditNote) return;
        try {
            const result = await legacyApi.sales.createCreditNoteShareLink(creditNote.cn_id, { channel: 'WHATSAPP' });
            const message = `Please find credit note ${creditNoteNumber}: ${result.share_url}`;
            const customers = await legacyApi.customers.list();
            const customer = customers.find((entry) => Number(entry.customer_id) === Number(creditNote.customer_id));
            const resolved = openWhatsAppMessage(message, customer);
            if (!resolved.normalizedNumber) {
                toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to open WhatsApp share');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between border-b bg-white px-4 py-3">
                <SendPdfModal
                    open={showSendPdfModal}
                    onOpenChange={setShowSendPdfModal}
                    documentLabel="Credit Note"
                    documentNumber={creditNoteNumber}
                    attachmentName={`${creditNoteNumber.replace(/\//g, '_')}.pdf`}
                    defaultSubject={`Credit Note ${creditNoteNumber}`}
                    defaultBody={`Dear Customer,\n\nPlease find attached credit note ${creditNoteNumber}.\n\nRegards,\nHADAF COMPOSITES`}
                    onSend={handleSendPdf}
                    resolveDefaultTo={resolveCreditNoteRecipientEmail}
                />
                <Button variant="ghost" onClick={() => navigate('/legacy/credit-notes')}>
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Credit Notes
                </Button>
                <div className="flex items-center gap-2">
                    {isDraft && (
                        <>
                            <Button variant="outline" disabled={busy !== null} onClick={() => handleTransition('cancel')}>
                                {busy === 'cancel' ? 'Cancelling...' : 'Cancel'}
                            </Button>
                            <Button disabled={busy !== null} onClick={() => handleTransition('post')}>
                                {busy === 'post' ? 'Posting...' : 'Post'}
                            </Button>
                        </>
                    )}
                    <Button variant="outline" onClick={() => legacyApi.sales.downloadCreditNotePDF(creditNote.cn_id)}>
                        <Download className="mr-2 h-4 w-4" /> Print
                    </Button>
                    {hasPermission('finance.credit_note.email') && (
                        <Button variant="outline" onClick={() => setShowSendPdfModal(true)}>
                            <Send className="mr-2 h-4 w-4" /> Send
                        </Button>
                    )}
                    {hasPermission('finance.credit_note.share') && (
                        <>
                            <Button variant="outline" onClick={handleShareLink}>
                                <Link2 className="mr-2 h-4 w-4" /> Share Link
                            </Button>
                            <Button variant="outline" onClick={handleShareWhatsApp}>
                                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="rounded-md border bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold">{creditNote.cn_number}</h1>
                        <p className="text-sm text-muted-foreground">
                            Date: {creditNote.cn_date ? new Date(creditNote.cn_date).toLocaleDateString() : '-'}
                        </p>
                    </div>
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                        {creditNote.status_name}
                    </span>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 text-sm">
                    <div>
                        <p className="text-muted-foreground">Customer</p>
                        <p className="font-medium">{creditNote.customer_name || `Customer #${creditNote.customer_id}`}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground">Source Invoice</p>
                        <Link to={`/legacy/invoices/${creditNote.source_invoice_id}`} className="font-mono text-blue-600 hover:underline">
                            {`HC/INV/${String(creditNote.source_invoice_id).padStart(4, '0')}`}
                        </Link>
                    </div>
                    <div>
                        <p className="text-muted-foreground">Posted Invoice Link</p>
                        {creditNote.posted_invoice_id ? (
                            <Link to={`/legacy/invoices/${creditNote.posted_invoice_id}`} className="font-mono text-blue-600 hover:underline">
                                {`HC/INV/${String(creditNote.posted_invoice_id).padStart(4, '0')}`}
                            </Link>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                </div>
                {(creditNote.reason || creditNote.remarks) && (
                    <div className="mt-3 rounded border bg-slate-50 p-3 text-sm">
                        {creditNote.reason && <p><span className="font-medium">Reason:</span> {creditNote.reason}</p>}
                        {creditNote.remarks && <p><span className="font-medium">Remarks:</span> {creditNote.remarks}</p>}
                    </div>
                )}
            </div>

            <div className="rounded-md border bg-white p-4">
                <h2 className="mb-3 text-lg font-semibold">Line Items</h2>
                <CompactTable data={creditNote.lines || []} columns={lineColumns} keyField="cn_detail_id" />
                <div className="ml-auto mt-4 max-w-sm space-y-1 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{Number(creditNote.subtotal || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>VAT ({Number(creditNote.vat_percent || 0).toFixed(2)}%)</span><span>{Number(creditNote.vat_amount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between border-t pt-2 text-base font-semibold"><span>Grand Total</span><span>{Number(creditNote.grand_total || 0).toFixed(2)}</span></div>
                </div>
            </div>

            <div className="rounded-md border bg-white p-4">
                <h2 className="mb-3 text-lg font-semibold">Status History</h2>
                <CompactTable data={creditNote.history || []} columns={historyColumns} keyField="log_id" />
            </div>
        </div>
    );
};
